import { faqs, howItWorks, AnalyticsHeader } from "./helpers";
import { routes } from "@/global/routes";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";

import SeatMap3dPreview from "public/images/milwaukeebucks/bucks-preview.jpg";

const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/NBA%202025/MILWAUKEE%20BUCKS/Milwaukee_Bucks_logo.png";
const FindGamesBanner =
  "https://ss-leagues.s3.amazonaws.com/NBA%202025/MILWAUKEE%20BUCKS/2025-26_PartialPlans_10-Game-FlexPlans_PromotionalGraphics_ATTEND_BannerHeaderImage_1486x128.jpg";

import type { TeamConfig } from "../types";

const config: TeamConfig = {
  teamSchema: "milwaukeebucks_flex",
  browserTitle: "Bucks 2025-26 Flex Plan",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Milwaukee Bucks",

  infoFieldsConfig: {
    camefrom: {
      infoField: 10,
      name: "camefrom",
      value: "VSAS",
    },
  },

  // seat map configs
  seatMapConfigs: {
    mapId: "blockmap2d",
    venueId: "nam-us-00067-bucks-sec",
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.seatLevel,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 3,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
    isPerksEnabled: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  packs: {
    enabled: true,
    dontOpenPackModal: true,
    disablePackSelectionLossModal: true,
    list: [
      {
        id: 1,
        defaultSelected: true,
        amount: 3,
        title: "3 Game Flex Plan",
        description:
          "Pick 3 games + get both 10% Arena Credit and choice of $50 Lyft or SpotHero credit for Transportation/Parking!",
        offerMoreMessage:
          "Switch to a 4 Game Flex Plan and get the Choice of either $50 Lyft or SpotHero Credit for Transportation/Parking!",
        buckets: [
          {
            id: 1,
            title: "Upcoming Games",
            description: "",
            max: 3,
            min: 3,
            displayAllEvents: true,
          },
        ],
        successMessage:
          "You're good to go! Click Continue to view seating options.",
        updatedAccountGroup: 0,
      },
      {
        id: 2,
        amount: 4,
        title: "4 Game Flex Plan",
        description:
          "Pick 4 games + get both 10% Arena Credit and choice of $50 Lyft or SpotHero credit for Transportation/Parking!",
        offerMoreMessage:
          "Switch to a 5 Game Flex Plan and get Both 10% Arena Credit and choice of $50 Lyft or SpotHero Credit for Transportation/Parking!",
        buckets: [
          {
            id: 1,
            title: "Upcoming Games",
            description: "",
            displayAllEvents: true,
            max: 4,
            min: 4,
          },
        ],
        successMessage:
          "You're good to go! Click Continue to view seating options.",
        updatedAccountGroup: 0,
      },
    ],
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    AnalyticsHeader,
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,
  manageCard: true,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/bucks/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage,
    authBackgroundImage: FindGamesBanner,
    findGamesBannerImage: FindGamesBanner,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
    checkoutStatusPageImage: FindGamesBanner,
    logoImage: LogoImage + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    checkoutStatusPage: {
      secondaryButton: {
        label: "Claim Your Credit",
        url: "https://pages.milwaukeebucks.com/Bucks-Landing-Pages_2025-flexmas-v3.html",
      },
    },
  },

  colors: {
    primary: "#00471B",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarAditionalText: " to continue!",
      progressBarSuccessText:
        "You're good to go! Add more games or select Continue to view seating options.",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: (
      <>
        Thank you for your Milwaukee Bucks purchase!
        <br />
        <br />
        Please click the form link below to receive your Lyft or SpotHero
        credit!
      </>
    ),

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
